/**
 * The railway's CURRENT working turns — read only.
 *
 * The "New" tab of the Working turns panel edits the built timetable; this is
 * the other half of the pair: what the railway actually runs today, straight
 * from thebigplan's working timetable, so the two can be read against each
 * other in one place (Chris, 2026-08-25: "I need to see current and new as
 * tabs on this working turns panel").
 *
 * Deliberately NOT the DepEditor: nothing here is editable, and showing
 * editing affordances that do nothing is exactly the kind of dishonesty this
 * app has been punished for.
 */
import type { Departure, Network } from './types'
import { fmtMin } from './demand'
import { journeyMin, routeLabel } from './network'

export function CurrentTurns({ net, deps }: { net: Network; deps: Departure[] }) {
  const rows = deps
    .filter((d) => d.enabled)
    .slice()
    .sort((a, b) => a.minutes - b.minutes)
  if (rows.length === 0) {
    return <p className="stu-note">No current working timetable loaded for this month — the Baseline stage shows why.</p>
  }
  const north = rows.filter((d) => d.d > d.o)
  const south = rows.filter((d) => d.d < d.o)
  const table = (title: string, list: Departure[]) => (
    <div>
      <div className="sh-section-label" style={{ marginBottom: 4 }}>{title}</div>
      {list.length === 0 ? <p className="stu-note">None.</p> : (
        <table className="stu-table">
          <thead>
            <tr><th>Dep</th><th>Journey</th><th>Arr</th><th className="num">Seats</th><th>Set</th></tr>
          </thead>
          <tbody>
            {list.map((d) => (
              <tr key={d.id}>
                <td style={{ fontWeight: 700 }}>{d.time}</td>
                <td>{routeLabel(net, d.o, d.d)}{d.positioning ? ' (empty stock)' : ''}</td>
                <td>{fmtMin(d.arrMin ?? d.minutes + journeyMin(net, d.o, d.d))}</td>
                <td className="num">{d.capacity ?? (d.coaches === 0 ? 48 : d.coaches * 64)}</td>
                <td>{d.setPin ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
  return (
    <>
      <p className="stu-note" style={{ marginTop: 0 }}>
        {rows.length} turn{rows.length === 1 ? '' : 's'} — the real working timetable for this month, as published. Read only; switch to <strong>New</strong> to edit the built timetable.
      </p>
      <div className="sh-grid2">
        {table('Northbound', north)}
        {table('Southbound', south)}
      </div>
    </>
  )
}
